import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SessionExpiredModal } from './SessionExpiredModal';
import { sessionManager } from '@/services/sessionManager';

export const SessionManagerBridge: React.FC = () => {
  const { user, status, hasAuthenticated } = useAuth();
  const [isExpiredOpen, setIsExpiredOpen] = useState(false);
  const location = useLocation();

  // Safety net: if we missed the event (mount timing), open the modal whenever
  // sessionManager is already in reauth-required mode.
  useEffect(() => {
    if (hasAuthenticated && sessionManager.isReauthRequired()) {
      setIsExpiredOpen(true);
    }
  }, [hasAuthenticated]);

  useEffect(() => {
    const unsubscribeRequired = sessionManager.on('reauth-required', () => {
      if (hasAuthenticated) {
        console.warn('[SessionManagerBridge] Reauth required, opening modal');
        setIsExpiredOpen(true);
      }
    });
    const unsubscribeSuccess = sessionManager.on('reauth-success', () => {
      console.info('[SessionManagerBridge] Reauth success, closing modal');
      setIsExpiredOpen(false);
    });
    const unsubscribeCancelled = sessionManager.on('reauth-cancelled', () => {
      console.warn('[SessionManagerBridge] Reauth cancelled, closing modal');
      setIsExpiredOpen(false);
    });

    return () => {
      unsubscribeRequired();
      unsubscribeSuccess();
      unsubscribeCancelled();
    };
  }, [hasAuthenticated]);

  useEffect(() => {
    const isAuthenticated = !!user && status === 'success';
    sessionManager.setAuthenticated(isAuthenticated);
    if (isAuthenticated) {
      sessionManager.start();
    } else {
      sessionManager.stop();
      if (!sessionManager.isReauthRequired()) {
        setIsExpiredOpen(false);
      }
    }
  }, [user, status, hasAuthenticated]);

  const pathname = location.pathname || '';
  const isOnLoginPage = pathname === '/login' || pathname.startsWith('/login');

  if (isOnLoginPage || (!user && !isExpiredOpen)) {
    return null;
  }

  return (
    <SessionExpiredModal
      isOpen={isExpiredOpen}
      onClose={() => setIsExpiredOpen(false)}
      onSessionRestored={() => setIsExpiredOpen(false)}
    />
  );
};
